/**
 * Use of force (response to resistance), personnel actions, and the early warning system (SC Act 218:
 * "an early-warning system for at-risk officer behavior"). Thresholds are the agency's to set; these
 * defaults follow common model policies (several uses of force in a short time, repeated complaints or
 * discipline, any firearm discharge or out-of-policy finding).
 */

export const FORCE_TYPES: Record<string, string> = {
  presence: 'Officer presence / verbal commands',
  soft_hands: 'Soft empty-hand control (escort, pressure points, joint locks)',
  takedown: 'Takedown',
  hard_hands: 'Hard empty-hand (strikes, kicks)',
  oc: 'OC / pepper spray',
  cew_display: 'Taser (CEW) displayed / arced',
  cew_drive: 'Taser (CEW) drive stun',
  cew_probe: 'Taser (CEW) probe deployment',
  impact: 'Baton / impact weapon',
  less_lethal: 'Less-lethal launcher (beanbag, 40mm)',
  k9: 'K-9 apprehension / bite',
  restraint: 'Restraint device (WRAP, leg restraints)',
  firearm_pointed: 'Firearm pointed at a person',
  firearm_discharged: 'Firearm discharged',
  vehicle: 'Vehicle (PIT, ramming, blocking)',
  other: 'Other',
};
/** Force that counts toward the early warning system (pointing a firearm and presence alone don't, by default). */
export const REPORTABLE_FORCE = Object.keys(FORCE_TYPES).filter((k) => k !== 'presence');

export const CONTACT_REASONS = ['Call for service', 'Traffic stop', 'Arrest', 'Warrant service', 'Civil process', 'Detention / jail', 'Mental health / ECO', 'Officer-initiated', 'Other'];
export const RESISTANCE_LEVELS: Record<string, string> = { passive: 'Passive resistance', active: 'Active resistance', aggressive: 'Aggressive / assaultive', deadly: 'Deadly force threat' };
export const UOF_FINDINGS: Record<string, string> = { within_policy: 'Within policy', within_policy_training: 'Within policy — training needed', not_within_policy: 'Not within policy', refer_ia: 'Referred to internal affairs' };

export const PERSONNEL_KINDS: Record<string, { label: string; tone: 'good' | 'bad' | 'neutral'; ews: string | null }> = {
  commendation: { label: 'Commendation', tone: 'good', ews: null },
  appreciation: { label: 'Letter of appreciation', tone: 'good', ews: null },
  counseling: { label: 'Documented counseling', tone: 'neutral', ews: 'counseling' },
  pip: { label: 'Performance improvement plan', tone: 'neutral', ews: 'counseling' },
  written_reprimand: { label: 'Written reprimand', tone: 'bad', ews: 'discipline' },
  suspension: { label: 'Suspension', tone: 'bad', ews: 'discipline' },
  demotion: { label: 'Demotion', tone: 'bad', ews: 'discipline' },
  termination: { label: 'Termination', tone: 'bad', ews: 'discipline' },
  complaint: { label: 'Citizen complaint', tone: 'bad', ews: 'complaint' },
  internal_complaint: { label: 'Internal complaint', tone: 'bad', ews: 'complaint' },
};
export const COMPLAINT_FINDINGS: Record<string, string> = { pending: 'Under investigation', sustained: 'Sustained', not_sustained: 'Not sustained', exonerated: 'Exonerated', unfounded: 'Unfounded', policy_failure: 'Policy failure' };

export interface EwsThreshold {
  count: number;
  days: number;
}
export const EWS_INDICATORS: Record<string, { label: string; default: EwsThreshold }> = {
  use_of_force: { label: 'Uses of force', default: { count: 3, days: 90 } },
  firearm_discharge: { label: 'Firearm discharge', default: { count: 1, days: 365 } },
  serious_injury: { label: 'Force with serious injury', default: { count: 1, days: 365 } },
  uof_out_of_policy: { label: 'Force found not within policy', default: { count: 1, days: 365 } },
  complaint: { label: 'Complaints', default: { count: 2, days: 365 } },
  sustained_complaint: { label: 'Sustained complaints', default: { count: 2, days: 730 } },
  discipline: { label: 'Discipline (reprimand or more)', default: { count: 2, days: 365 } },
  counseling: { label: 'Documented counseling / PIP', default: { count: 3, days: 365 } },
  total: { label: 'All indicators together', default: { count: 5, days: 365 } },
};

export interface EwsEvent {
  id: string;
  indicator: string;
  at: string; // ISO
  label: string;
}

/** Which indicators an officer has crossed, from their events and the agency's thresholds. */
export function ewsEvaluate(events: EwsEvent[], thresholds: Record<string, EwsThreshold>, now: Date) {
  const out: { indicator: string; count: number; threshold: number; days: number; items: EwsEvent[] }[] = [];
  for (const [ind, meta] of Object.entries(EWS_INDICATORS)) {
    const t = thresholds[ind] ?? meta.default;
    if (!t || t.count <= 0) continue;
    const since = +now - t.days * 86_400_000;
    const items = events.filter((e) => (ind === 'total' ? e.indicator !== 'firearm_discharge' && e.indicator !== 'sustained_complaint' && e.indicator !== 'serious_injury' && e.indicator !== 'uof_out_of_policy' : e.indicator === ind) && +new Date(e.at) >= since);
    if (items.length >= t.count) out.push({ indicator: ind, count: items.length, threshold: t.count, days: t.days, items: items.sort((a, b) => b.at.localeCompare(a.at)) });
  }
  return out;
}

export const EWS_OUTCOMES: Record<string, string> = {
  no_action: 'Reviewed — no action needed',
  counseling: 'Supervisor counseling',
  training: 'Remedial training',
  leap: 'Referred to LEAP / EAP',
  reassignment: 'Reassignment',
  fitness: 'Fitness-for-duty evaluation',
  ia: 'Referred to internal affairs',
  other: 'Other',
};
